import { useState } from "react";
import { Plus } from "lucide-react";
import type { Alert } from "@snipe/core";
import { AlertForm, draftFromAlert, emptyDraft } from "@/components/alert-form";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardNote, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/empty-state";
import { Loading, LoadFailure } from "@/components/query-state";
import { StatusChip } from "@/components/status";
import { TBody, TD, TH, THead, TR, Table } from "@/components/ui/table";
import {
  useAlertMatches,
  useAlerts,
  useCreateAlert,
  useDeleteAlert,
  usePatchAlert,
} from "@/lib/queries";
import { formatDuration, formatRelative, formatStamp } from "@/lib/time";
import { useNow } from "@/lib/use-now";

export function AlertsPage() {
  const now = useNow();
  const alerts = useAlerts();
  const create = useCreateAlert();
  const patch = usePatchAlert();
  const remove = useDeleteAlert();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Alert | null>(null);
  const [selected, setSelected] = useState<Alert | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const matches = useAlertMatches(selected?.id ?? null);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-base font-semibold">Alerts</h1>
        <Button
          className="ml-auto"
          size="sm"
          onClick={() => {
            setEditing(null);
            setCreating((open) => !open);
          }}
        >
          <Plus aria-hidden="true" className="h-3.5 w-3.5" />
          New alert
        </Button>
      </div>

      {creating ? (
        <Card>
          <CardHeader>
            <CardTitle>New alert</CardTitle>
            <CardNote>Runs as soon as you save it.</CardNote>
          </CardHeader>
          <CardBody>
            <AlertForm
              initial={emptyDraft()}
              submitLabel={create.isPending ? "Creating…" : "Create alert"}
              busy={create.isPending}
              onCancel={() => setCreating(false)}
              onSubmit={(input) =>
                create.mutate(input, { onSuccess: () => setCreating(false) })
              }
            />
            {create.error !== null ? (
              <p role="alert" className="mt-3 text-sm font-medium text-danger">
                {create.error.message}
              </p>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      {editing !== null ? (
        <Card>
          <CardHeader>
            <CardTitle>Edit “{editing.name}”</CardTitle>
          </CardHeader>
          <CardBody>
            <AlertForm
              initial={draftFromAlert(editing)}
              submitLabel={patch.isPending ? "Saving…" : "Save changes"}
              busy={patch.isPending}
              onCancel={() => setEditing(null)}
              onSubmit={(input) =>
                patch.mutate(
                  {
                    id: editing.id,
                    name: input.name,
                    filters: input.filters,
                    backfillDays: input.backfillDays,
                    ...(input.intervalSeconds === undefined
                      ? {}
                      : { intervalSeconds: input.intervalSeconds }),
                  },
                  { onSuccess: () => setEditing(null) },
                )
              }
            />
            {patch.error !== null ? (
              <p role="alert" className="mt-3 text-sm font-medium text-danger">
                {patch.error.message}
              </p>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Your alerts</CardTitle>
          <CardNote>{alerts.data?.length ?? 0} on this account</CardNote>
        </CardHeader>

        {alerts.isPending ? <Loading what="alerts" /> : null}
        {alerts.error !== null ? (
          <LoadFailure what="alerts" error={alerts.error} onRetry={() => void alerts.refetch()} />
        ) : null}

        {alerts.data !== undefined && alerts.data.length === 0 ? (
          <EmptyState title="No alerts on this account.">
            Press <span className="font-medium">New alert</span> to make one.
          </EmptyState>
        ) : null}

        {alerts.data !== undefined && alerts.data.length > 0 ? (
          <CardBody className="p-0">
            <Table>
              <THead>
                <TR>
                  <TH>Name</TH>
                  <TH>Query</TH>
                  <TH className="text-right">Every</TH>
                  <TH>State</TH>
                  <TH>Last run</TH>
                  <TH>Next due</TH>
                  <TH><span className="sr-only">Actions</span></TH>
                </TR>
              </THead>
              <TBody>
                {alerts.data.map((alert) => (
                  <TR key={alert.id}>
                    <TD className="font-medium">
                      <button
                        type="button"
                        className="underline underline-offset-4"
                        onClick={() => setSelected(alert)}
                      >
                        {alert.name}
                      </button>
                    </TD>
                    <TD className="text-muted-foreground">
                      {alert.filters.query}
                      {alert.filters.excludeKeywords.length > 0 ? (
                        <span className="block text-xs">
                          minus {alert.filters.excludeKeywords.join(", ")}
                        </span>
                      ) : null}
                    </TD>
                    <TD data-numeric className="text-right">
                      {formatDuration(alert.intervalSeconds * 1000)}
                    </TD>
                    <TD>
                      <StatusChip
                        tone={alert.active ? "ok" : "muted"}
                        icon={alert.active ? "default" : "pause"}
                      >
                        {alert.active ? "active" : "paused"}
                      </StatusChip>
                    </TD>
                    <TD data-numeric className="whitespace-nowrap text-muted-foreground">
                      {alert.lastRunAt === null ? "never" : formatRelative(alert.lastRunAt, now)}
                    </TD>
                    <TD data-numeric className="whitespace-nowrap text-muted-foreground">
                      {alert.nextDueAt === null ? "-" : formatStamp(alert.nextDueAt)}
                    </TD>
                    <TD>
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => patch.mutate({ id: alert.id, active: !alert.active })}
                        >
                          {alert.active ? "Pause" : "Resume"}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setCreating(false);
                            setEditing(alert);
                          }}
                        >
                          Edit
                        </Button>
                        {/* Two taps rather than a dialog: deleting your own
                            test alert is recoverable by making another one,
                            unlike burning a session. */}
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => {
                            if (confirmDelete === alert.id) {
                              remove.mutate(alert.id);
                              setConfirmDelete(null);
                              if (selected?.id === alert.id) setSelected(null);
                            } else {
                              setConfirmDelete(alert.id);
                            }
                          }}
                        >
                          {confirmDelete === alert.id ? "Confirm delete" : "Delete"}
                        </Button>
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </CardBody>
        ) : null}
      </Card>

      {selected !== null ? (
        <Card>
          <CardHeader>
            <CardTitle>Matches for “{selected.name}”</CardTitle>
            <CardNote>
              <button type="button" className="underline underline-offset-4" onClick={() => setSelected(null)}>
                Close
              </button>
            </CardNote>
          </CardHeader>

          {matches.isPending ? <Loading what="matches" /> : null}
          {matches.error !== null ? (
            <LoadFailure what="matches" error={matches.error} onRetry={() => void matches.refetch()} />
          ) : null}

          {matches.data !== undefined && matches.data.length === 0 ? (
            <EmptyState title="Nothing matched yet.">
              Check the run log for this alert.
            </EmptyState>
          ) : null}

          {matches.data !== undefined && matches.data.length > 0 ? (
            <CardBody className="p-0">
              <Table>
                <THead>
                  <TR>
                    <TH>Matched</TH>
                    <TH>Listing</TH>
                    <TH className="text-right">Price</TH>
                    <TH>Where</TH>
                    <TH>Kind</TH>
                  </TR>
                </THead>
                <TBody>
                  {matches.data.map((match) => (
                    <TR key={match.id}>
                      <TD data-numeric className="whitespace-nowrap text-muted-foreground">
                        {formatRelative(match.matchedAt, now)}
                      </TD>
                      <TD>{match.listing.title ?? "(untitled)"}</TD>
                      <TD data-numeric className="text-right">
                        {match.listing.priceMinor === null
                          ? "-"
                          : `${match.listing.currency ?? ""} ${(match.listing.priceMinor / 100).toFixed(2)}`}
                      </TD>
                      <TD className="text-muted-foreground">{match.listing.city ?? "-"}</TD>
                      <TD>
                        <StatusChip tone={match.backfill ? "muted" : "ok"}>
                          {match.backfill ? "backfill" : "new"}
                        </StatusChip>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </CardBody>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}
